import React from 'react';
import styled, { css } from 'styled-components';
import ConsignmentEntry from 'data/ConsignmentEntry';
import { CANCELED, MANUFACTURING, PROCESSING } from 'constants/consignmentStates';
import { Background, Border, Main, Text } from 'styles/themeColors';
import { formatDateTime, currencySign } from 'utils/formatters';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import { SHIPPING } from 'constants/deliveryModes';
import { IN_STOCK } from 'constants/consignmentEntry';
import { CONSIGNMENT_TYPES } from 'constants/order';
import BadgePill from 'components/Settings/ProductBadges/BadgePill';
import { getPillTheme } from 'utils/consignment';
import Pill from 'components/UI/Pill';
import { useCountries } from 'hooks/useCountries';

const TableFooterColumn = styled.span<any>`
  width: 200px;
  height: 20px;
  font-style: normal;
  font-weight: bold;
  color: ${Text.primary};
  font-size: 16px;
  line-height: 20px;
`;

const TableFooterData = styled.span<any>`
  width: 104px;
  height: 20px;
  text-align: right;
  color: ${Text.primary};
  font-size: 16px;
  line-height: 20px;
`;

const Surcharges = styled.span<any>`
  font-style: italic;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: ${Text.primary};
`;

const TableColumn = styled.span<any>`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  min-height: 18px;
  width: 100%;
  color: ${Text.secondary};
  margin-bottom: 4px;

  ${({ color }) => color && css`
  color: ${color};
`}

${({ textDecoration }) => textDecoration && css`
  text-decoration: ${textDecoration};
`}

${({ fontSize }) => fontSize && css`
  font-size: ${fontSize};
`}

${({ fontWeight }) => fontWeight && css`
  font-weight: ${fontWeight};
`}

${({ deleted }) => deleted && css`
  opacity: 0.5;
`}

${({ width }) => width && css`
  width: ${width};
`}
`;

const mapLabelForEntry = (type: string) => {
  switch (type) {
    case PROCESSING:
      return 'In stock';
    case MANUFACTURING:
      return 'Manufacturing';
    case CANCELED:
      return 'Canceled';
    default:
      return '';
  }
}


interface Props {
  consignmentEntries: ConsignmentEntry[];
  isPreparationStep?: boolean;
  totalDeliveryCost?: number;
  totalPriceWithTax?: number;
  state: string;
  showSummary?: boolean;
  subTotal?: number;
  deliveryDate?: string;
  isRental?: boolean;
  consignmentId?: string;
  deliveryTax?: number;
  deliveryCost?: number;
  orderTotalCost?: number;
  fulfillmentRequest: ConsignmentEntry[];
  totalDiscounts?: number;
};

// Styled Components for consistent styling
const ProductContainer = styled.div<any>`
  display: flex;
  gap: 14px;
  align-items: flex-start;
`;

const ProductImage = styled.img`
  max-height: 96px;
  max-width: 96px;
  border: 1px solid #e1e6eb;
  object-fit: contain;
`;

const ProductDetails = styled.div`
  font-size: 14px;
  color: ${Text.primary};
  text-align: left;
  line-height: 1.5;
  opacity: 0.8;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
`;

const TableHeader = styled.th`
  padding: 12px;
  background-color: #f8f9fa;
  font-weight: bold;
  font-size: 14px;
  text-align: left;
  border: 1px solid #dee2e6;
`;

const TableData = styled.td`
  padding: 12px;
  font-size: 14px;
  color: ${Text.primary};
  border: 1px solid #dee2e6;
  vertical-align: top;
`;

const Consignment = styled.span<any>`
  height: 20px;
  font-style: normal;
  color: ${Text.primary};
  font-size: 16px;
  line-height: 20px;
`;

const splitColumnView = (key: string, value: any, color?: string) => <FlexboxContainerComponent alignItems='center'>
  <TableColumn width={'100px'}>{key}</TableColumn>
  <TableColumn color={color || Text.primary} width={'auto'}>{value}</TableColumn>
</FlexboxContainerComponent>

const OrderPreviewItem = ({
  consignmentEntries,
  state,
  showSummary = true,
  deliveryDate,
  isRental,
  consignmentId,
  deliveryCost = 0,
  deliveryTax = 0,
  orderTotalCost = 0,
  fulfillmentRequest = [],
  totalDiscounts = 0
}: Props) => {

  // Get Value from param for currency differentiate
  const queryParameters = new URLSearchParams(window.location.search);
  const country = queryParameters.get("country");
  let orderSubTotalPrice = 0;
  let totalTax = 0;
  let totalSurcharges = 0;

  const { countries } = useCountries();

  const productColumn = (consignmentEntry: ConsignmentEntry) => {
    const countryId = countries.find(x => x.countryIso === country)?.id;
    const unitOfMeasurement = consignmentEntry?.unitOfMeasurement?.displayName;
    const pricePerUnit = consignmentEntry?.basePrice;

    const mappedEntry = fulfillmentRequest.find(x => consignmentEntry.entryNumber === x.entryNumber);
    const rentalStartDate = mappedEntry?.rentalStartDate;
    const rentalEndDate = mappedEntry?.rentalEndDate;

    return (<>
      <TableData style={{ width: '70%' }}>
        <ProductContainer>
          {
            consignmentEntry.parentEntryNumber === null ?
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {
                  consignmentEntry.badge &&
                  <span style={{ alignSelf: 'flex-start', margin: '0px 0px 10px 0px' }}>
                    <BadgePill name={consignmentEntry.badge.name} colorHash={consignmentEntry.badge.colorHash} />
                  </span>
                }
                <ProductImage
                  src={consignmentEntry.productImages && consignmentEntry.productImages.length > 0 ? process.env.REACT_APP_MEDIA_URL! + consignmentEntry.productImages[0].path : ''}
                  alt="Product"
                />
              </div> :
              <div style={{ width: 100 }}>

              </div>
          }
          <ProductDetails>
            {/* Product name */}
            <div style={{ fontWeight: 'bold' }}>{consignmentEntry.productName}</div>

            {/* Product sku */}
            <div style={{ opacity: 0.7 }}>
              {
                splitColumnView('SKU', consignmentEntry.productSKU)
              }
            </div>


            {/* Serial number */}
            <div style={{ opacity: 0.7 }}>
              {
                splitColumnView('Serial Number', consignmentEntry.serialNumber || 'N/A')
              }
            </div>

            {/* Product attributes */}
            {consignmentEntry.selectableAttributes?.map((attr) => {
              const atrValue = consignmentEntry.attributeValues.find(val => val.attributeId === attr.id);

              return (
                <div key={attr.id} style={{ opacity: 0.7 }}>
                  {
                    splitColumnView(attr.name, atrValue ? atrValue.name : '')
                  }
                </div>
              )
            })}

            {/* Pricebooks */}
            {
              consignmentEntry.pricebookName && consignmentEntry.pricebookId &&
              <div style={{ opacity: 0.7 }}>
                {
                  splitColumnView('Pricebook name / ID', `${consignmentEntry.pricebookName} / ${consignmentEntry?.priceBook?.PricebookVendorId}`)
                }
              </div>
            }

            <div>
              {
                splitColumnView(consignmentEntry.deliveryMode === SHIPPING ? "Est. ship date" : "Est. pickup date", formatDateTime(deliveryDate, false, false))
              }
            </div>

            {/* Rental dates */}
            {consignmentEntry.rentalStartDate && consignmentEntry.rentalEndDate && (
              <div>
                {
                  splitColumnView('Date range', formatDateTime(rentalStartDate, false, false) + " - " + formatDateTime(rentalEndDate, false, false))
                }
              </div>
            )}

            {consignmentEntry?.productVariantPrices && pricePerUnit && unitOfMeasurement && (
              <div>
                {
                  splitColumnView('Price rate', `${currencySign(country, pricePerUnit!)} / ${unitOfMeasurement}`, Main.brandDarkest)
                }
              </div>
            )}

          </ProductDetails>
        </ProductContainer>
      </TableData>
    </>)
  };

  const addonColumn = (consignmentEntry: ConsignmentEntry, title?: string) => <>
    <TableData style={{ width: '70%' }}>
      <ProductContainer style={{ marginLeft: 120 }}>
        <ProductDetails>
          {/* Product name */}
          {
            title &&
            <div style={{ fontWeight: 'bold', marginBottom: 10 }}>{title}</div>
          }
          <div style={{ fontWeight: 'bold' }}>{consignmentEntry.productName}</div>

          {/* Product sku */}
          <div style={{ opacity: 0.7 }}>
            {
              splitColumnView('SKU', consignmentEntry.productSKU)
            }
          </div>

          {/* unit of measurement */}
          <div style={{ opacity: 0.7 }}>
            {
              splitColumnView('Unit', consignmentEntry.unitOfMeasurement?.displayName)
            }
          </div>

        </ProductDetails>
      </ProductContainer>
    </TableData>
  </>

  const quantityColumn = (consignmentEntry: ConsignmentEntry) => <>
    <TableData style={{ width: '10%' }}>{consignmentEntry?.quantity}</TableData>
  </>;

  const unitPriceColumn = (consignmentEntry: ConsignmentEntry) => <>
    {
      <TableData style={{ width: '10%' }}>{currencySign(country, consignmentEntry.basePrice !== consignmentEntry.discountedPrice ? consignmentEntry.discountedPrice : consignmentEntry.basePrice)}</TableData>
    }
  </>;


  const subTotalColumn = (consignmentEntry: ConsignmentEntry) => {

    totalTax += consignmentEntry?.taxAmount || 0;
    totalSurcharges += consignmentEntry?.surcharge || 0;
    orderSubTotalPrice += consignmentEntry.totalPrice || 0;
    const unitOfMeasurement = consignmentEntry?.unitOfMeasurement?.displayName;

    return <>
      <TableData style={{ width: '10%' }}>
        {currencySign(country, consignmentEntry.totalPrice)}
        {
          consignmentEntry.surcharge > 0
            ? <><br /><span>+{currencySign(country, consignmentEntry.surcharge)} <br /> surcharge</span></>
            : <span>&nbsp;</span>
        }
        {
          consignmentEntry.plannedUsage > 0
            ? <><br /><br /><span>Planned usage: {consignmentEntry.plannedUsage} {unitOfMeasurement}</span></>
            : <span>&nbsp;</span>
        }
      </TableData>
    </>
  };

  const rows: React.ReactNode[] = [];

  const entryMap = new Map<number, ConsignmentEntry[]>();

  // Group entries by parentEntryNumber
  consignmentEntries.forEach(entry => {
    const key = entry.parentEntryNumber;
    if (key) {
      if (!entryMap.has(key)) {
        entryMap.set(key, []);
      }
      entryMap.get(key)!.push(entry);
    }
  });

  const mainEntries = consignmentEntries.filter(entry => entry.parentEntryNumber === null);

  mainEntries.forEach(mainEntry => {
    rows.push(
      <tr key={mainEntry.id}>
        {productColumn(mainEntry)}
        {quantityColumn(mainEntry)}
        {unitPriceColumn(mainEntry)}
        {subTotalColumn(mainEntry)}
      </tr>
    );

    const addons = entryMap.get(mainEntry.entryNumber) || [];

    const requiredAddons = addons.filter(x => x.requiredAddon);
    const optionalAddons = addons.filter(x => !x.requiredAddon);

    requiredAddons.forEach((addon, index) => {
      const isRequired = addon.requiredAddon;
      rows.push(
        <tr key={addon.id} style={{ backgroundColor: !isRequired ? Background.shaded : Background.light }}>
          {addonColumn(addon, index === 0 ? 'Product Add-ons' : '')}
          {quantityColumn(addon)}
          {unitPriceColumn(addon)}
          {subTotalColumn(addon)}
        </tr>
      );
    });

    optionalAddons.forEach((addon, index) => {
      const isRequired = addon.requiredAddon;
      rows.push(
        <tr key={addon.id} style={{ backgroundColor: !isRequired ? Background.shaded : Background.light }}>
          {addonColumn(addon, index === 0 ? 'Optional Add-ons' : '')}
          {quantityColumn(addon)}
          {unitPriceColumn(addon)}
          {subTotalColumn(addon)}
        </tr>
      );
    });
  });

  const orderTotal = showSummary && (
    <FlexboxContainerComponent flexDirection="column" alignItems='flex-end' width='100%' padding='16px'>
      <FlexboxContainerComponent padding='16px'>
        <TableFooterColumn>Subtotal</TableFooterColumn>
        <TableFooterData> {orderSubTotalPrice && currencySign(country, orderSubTotalPrice)}</TableFooterData>
      </FlexboxContainerComponent>
      {
        totalDiscounts && totalDiscounts > 0
          ? (
            <FlexboxContainerComponent padding='16px'>
              <Surcharges>includes savings</Surcharges>
              <TableFooterData> -{totalDiscounts && currencySign(country, totalDiscounts)}</TableFooterData>
            </FlexboxContainerComponent>
          )
          : null
      }
      {
        totalSurcharges && totalSurcharges > 0
          ? (
            <FlexboxContainerComponent padding='16px'>
              <Surcharges>includes surcharges</Surcharges>
              <TableFooterData> +{totalSurcharges && currencySign(country, totalSurcharges)}</TableFooterData>
            </FlexboxContainerComponent>
          )
          : null
      }
      {
        deliveryCost ? (
          <FlexboxContainerComponent padding='16px' borderTop={`1px solid ${Border.light}`} borderBottom={`1px solid ${Border.light}`}>
            <TableFooterColumn fontSize="15px">Shipping</TableFooterColumn>
            <TableFooterData fontSize="15px">{currencySign(country, deliveryCost)}</TableFooterData>
          </FlexboxContainerComponent>
        ) : null
      }
      {
        totalTax ? (
          <FlexboxContainerComponent borderTop='1px solid #E1E6EB' padding='16px'>
            <TableFooterColumn>Tax</TableFooterColumn>
            <TableFooterData> {totalTax && currencySign(country, totalTax + deliveryTax)}</TableFooterData>
          </FlexboxContainerComponent>
        ) : null
      }
      <FlexboxContainerComponent borderTop={`2px solid ${Background.navy}`} padding='16px'>
        <TableFooterColumn>Order total</TableFooterColumn>
        <TableFooterData> {currencySign(country, orderTotalCost)}</TableFooterData>
      </FlexboxContainerComponent>
    </FlexboxContainerComponent>
  )

  const pillTheme = getPillTheme(state);

  const pill = <Pill margin="10px 0px" theme={pillTheme} bgColor={pillTheme} size="xsPlus" padding="4px">{mapLabelForEntry(state)}</Pill>;

  const consignmentEntry = fulfillmentRequest.find(x => x.parentEntryNumber === null && consignmentEntries.find(y => y.entryNumber === x.entryNumber));
  const rentalStartDate = consignmentEntry?.rentalStartDate;
  const rentalEndDate = consignmentEntry?.rentalEndDate;

  return (
    <>
      <FlexboxContainerComponent flexDirection='column' gap='10px' margin='0px'>
        <FlexboxContainerComponent flexDirection='row' gap='10px' margin='0px' alignItems='center'>
          <Consignment>Order # {consignmentId?.slice(4)}</Consignment>
          {pill}
          {state !== CANCELED && <FlexboxContainerComponent alignItems='center'>
            <TableColumn color={Text.primary} width={'134px'}>{consignmentEntries[0]?.deliveryMode === SHIPPING ? "Est. ship date" : "Est. pickup date"}</TableColumn>
            <TableColumn color={Text.primary} width={'134px'}>{formatDateTime(deliveryDate, false, false)}</TableColumn>
          </FlexboxContainerComponent>
          }
        </FlexboxContainerComponent>

        {splitColumnView('Start rent date', formatDateTime(rentalStartDate, false, false))}
        {splitColumnView('End rent date', formatDateTime(rentalEndDate, false, false))}

      </FlexboxContainerComponent>

      <p style={{ padding: 0 }}>{'Order items'}</p>

      <Table style={{ padding: 0, margin: 0 }}>
        <thead>
          <tr>
            <TableHeader style={{ width: '70%' }}>Product</TableHeader>
            <TableHeader style={{ width: '10%' }}>Quantity</TableHeader>
            <TableHeader style={{ width: '10%' }}>Unit price</TableHeader>
            <TableHeader style={{ width: '10%' }}>Subtotal</TableHeader>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </Table>
      {orderTotal}
    </>
  );
};

export default OrderPreviewItem;
